/**
 * PLAN-011 Phase 1.1: Centralized MUI Theme
 *
 * Single source of truth for all colors, typography, and component overrides.
 * Replaces the 12 inline createTheme instances scattered across the codebase.
 *
 * Usage:
 *   import { theme } from "../app/theme";  // from any file
 *   // Already wrapped in <ThemeProvider> at root level (index.js)
 *   // Use theme.palette.X.Y or useTheme() hook in components
 */
import { createTheme } from "@mui/material/styles";

const { palette } = createTheme();
const { augmentColor } = palette;
const color = (main) => augmentColor({ color: { main } });

export const theme = createTheme({
  // ── Typography ──────────────────────────────────────────────────────
  typography: {
    fontFamily: '"Mark Pro", sans-serif',
  },

  // ── Palette ─────────────────────────────────────────────────────────
  palette: {
    // Core brand colors
    primary: color("#075C87"),
    secondary: color("#3868a8"),

    // Role accent colors
    professional: color("#2a7297"),
    ong: color("#404c74"),

    // UI accents
    accent: color("#FEDA9D"),
    gold: color("#E0920C"),
    orange: color("#f5ad2f"),

    // Status colors (extend MUI defaults)
    success: { main: "#2e7d32" },
    error: { main: "#d32f2f" },
    warning: { main: "#ffab00" },
    info: { main: "#1976d2" },

    // Backgrounds
    background: {
      default: "#f5f5f5",
      paper: "#ffffff",
      warm: "#FFFBF5",
      muted: "#e9ecef",
    },

    // Text
    text: {
      primary: "#414941",
      secondary: "#5E5B5B",
      light: "#424242",
    },

    // Extra colors used by specific components (progress bars, form themes)
    blue: color("#586f7a"),
    green: color("#586f7a"),
    brown: color("#f5ad2f"),
  },

  // ── Shape ───────────────────────────────────────────────────────────
  shape: {
    borderRadius: 8,
  },

  // ── Component overrides ─────────────────────────────────────────────
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

export default theme;
